import translate from '@translate';
import React, { FC } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import { ReceivingType } from 'src/types/receiving';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Typography from 'src/components/Typography';
import AutocompleteTextField from 'src/components/AutocompleteTextField';
import Tooltip from '../../components/Tooltip';
import { InfoIcon } from '../../components/SkladonIcon/Icons';
import { styled } from '@mui/material/styles';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';

export interface DetailPropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: ReceivingType;
    editErrors: FormErrorsType;
    newReceiving: boolean;
    disableEdit: boolean;
    receivingDeliveryTypeCodes: ConstantsCodeType[];
    boolNumberCodes: ConstantsCodeType[];
    receivingTypeCodes: ConstantsCodeType[];
    incotermsCodes: ConstantsCodeType[];
}

const InfoIconStyled = styled('span')({
    marginLeft: '10px',

    '& svg': {
        width: '14px',
        height: '14px',
        marginRight: '10px'
    }
});

const Detail: FC<DetailPropsType> = (props) => {
    return (
        <Grid>
            <GridItem cols={12}>
                <Typography variant="h2">{translate('receivingDetail.supplier')}</Typography>
            </GridItem>
            <GridItem cols={12}>
                <AutocompleteTextField
                    value={props.editData.SUPPLIER}
                    error={props.editErrors.SUPPLIER}
                    onChange={(value, error) => props.onChange('SUPPLIER', value, error)}
                    name="SUPPLIER"
                    label={translate('receivingDetail.suplplier')}
                    fullWidth
                    required
                    maxLength={100}
                    disabled={props.disableEdit}
                    autoComplete="RECEIVING_SUPPLIER"
                />
            </GridItem>
            <GridItem cols={12}>
                <TextField
                    value={props.editData.DELIVERY_NOTE_NUMBER}
                    error={props.editErrors.DELIVERY_NOTE_NUMBER}
                    onChange={(value, error) => props.onChange('DELIVERY_NOTE_NUMBER', value, error)}
                    name="DELIVERY_NOTE_NUMBER"
                    label={translate('receivingDetail.deliveryNoteNumber')}
                    fullWidth
                    required
                    maxLength={100}
                    disabled={props.disableEdit}
                />
            </GridItem>
            <GridItem cols={12}>
                <Select
                    options={props.receivingDeliveryTypeCodes}
                    value={props.editData.DELIVERY_TYPE}
                    error={props.editErrors.DELIVERY_TYPE}
                    onChange={(value, error) => props.onChange('DELIVERY_TYPE', value || null, error)}
                    name="DELIVERY_TYPE"
                    label={<>
                        {translate('receivingDetail.deliveryType')}
                        <Tooltip title={translate('receivingDetail.deliveryTypeTooltip')}>
                            <InfoIconStyled>
                                <InfoIcon />
                            </InfoIconStyled>
                        </Tooltip>
                    </>}
                    fullWidth
                    required
                    disabled={props.disableEdit}
                />
            </GridItem>
            {props.editData.DELIVERY_TYPE === 'container' && (
                <GridItem cols={12}>
                    <TextField
                        value={props.editData.CONTAINER_NUMBER}
                        error={props.editErrors.CONTAINER_NUMBER}
                        onChange={(value, error) => props.onChange('CONTAINER_NUMBER', value, error)}
                        name="CONTAINER_NUMBER"
                        label={translate('receivingDetail.containerNumber')}
                        fullWidth
                        required={props.editData.DELIVERY_TYPE === 'container'}
                        maxLength={255}
                        disabled={props.disableEdit}
                    />
                </GridItem>
            )}
            <GridItem cols={12}>
                <TextField
                    value={props.editData.ASN_LABEL_EMAIL}
                    error={props.editErrors.ASN_LABEL_EMAIL}
                    onChange={(value, error) => props.onChange('ASN_LABEL_EMAIL', value, error)}
                    name="ASN_LABEL_EMAIL"
                    label={<>
                        {translate('receivingDetail.asnLabelEmail')}
                        <Tooltip title={translate('receivingDetail.asnLabelEmailTooltip')}>
                            <InfoIconStyled>
                                <InfoIcon />
                            </InfoIconStyled>
                        </Tooltip>
                    </>}
                    fullWidth
                    maxLength={255}
                    disabled={!props.newReceiving || props.disableEdit}
                    type="email"
                />
            </GridItem>
            <GridItem cols={12}>
                <Select
                    options={props.incotermsCodes}
                    value={props.editData.INCOTERMS}
                    error={props.editErrors.INCOTERMS}
                    onChange={(value, error) => props.onChange('INCOTERMS', value, error)}
                    name="INCOTERMS"
                    label={translate('receivingDetail.incoterms')}
                    fullWidth
                    disabled={props.disableEdit}
                />
            </GridItem>
            <GridItem cols={12}>
                <Typography variant="h2" sx={{ marginTop: '64px' }}>
                    {translate('receivingDetail.content')}
                </Typography>
            </GridItem>
            <GridItem cols={12}>
                <Select
                    options={props.receivingTypeCodes}
                    value={props.editData.RECEIVING_TYPE}
                    error={props.editErrors.RECEIVING_TYPE}
                    onChange={(value, error) => props.onChange('RECEIVING_TYPE', value, error)}
                    name="RECEIVING_TYPE"
                    label={<>
                        {translate('receivingDetail.receivingType')}
                        <Tooltip title={translate('receivingDetail.receivingTypeTooltip')}>
                            <InfoIconStyled>
                                <InfoIcon />
                            </InfoIconStyled>
                        </Tooltip>
                    </>}
                    fullWidth
                    required
                    disabled={props.disableEdit}
                />
            </GridItem>
            <GridItem cols={12}>
                <TextField
                    value={props.editData.UNITS_AMOUNT}
                    error={props.editErrors.UNITS_AMOUNT}
                    onChange={(value, error) => props.onChange('UNITS_AMOUNT', value, error)}
                    name="UNITS_AMOUNT"
                    label={translate('receivingDetail.amountOfHandlingUnits')}
                    fullWidth
                    required
                    type="numberLocale"
                    step="1"
                    min={1}
                    disabled={props.disableEdit}
                />
            </GridItem>
            <GridItem cols={12}>
                <TextField
                    value={props.editData.ANNOUNCED_PALLETS_COUNT}
                    error={props.editErrors.ANNOUNCED_PALLETS_COUNT}
                    onChange={(value, error) =>
                        props.onChange(
                            'ANNOUNCED_PALLETS_COUNT',
                            value !== '' && value != null ? parseInt(String(value)) : null,
                            error
                        )
                    }
                    name="ANNOUNCED_PALLETS_COUNT"
                    label={<>
                        {translate('receivingDetail.announcedPalletsCount')}
                        <Tooltip title={translate('receivingDetail.announcedPalletsCountTooltip')}>
                            <InfoIconStyled>
                                <InfoIcon />
                            </InfoIconStyled>
                        </Tooltip>
                    </>}
                    fullWidth
                    type="numberLocale"
                    step="1"
                    min={0}
                    required
                    disabled={props.disableEdit}
                />
            </GridItem>
            {!props.newReceiving && props.editData.STATUS !== 'awaiting' && (
                <>
                    <GridItem cols={12}>
                        <Typography variant="h2" sx={{ marginTop: '64px' }}>
                            {translate('receivingDetail.dcixInfo')}
                        </Typography>
                    </GridItem>
                    <GridItem cols={12}>
                        <TextField
                            value={props.editData.DELIVERED_CARTONS_COUNT}
                            name="DELIVERED_CARTONS_COUNT"
                            label={translate('receivingDetail.deliveredCartonsCount')}
                            fullWidth
                            disabled
                            type="numberLocale"
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <TextField
                            value={props.editData.DELIVERED_PALLETS_COUNT}
                            name="DELIVERED_PALLETS_COUNT"
                            label={translate('receivingDetail.deliveredPalletsCount')}
                            fullWidth
                            disabled
                            type="numberLocale"
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <TextField
                            value={props.editData.RETURNED_EUR_PALLETS_COUNT}
                            name="RETURNED_EUR_PALLETS_COUNT"
                            label={translate('receivingDetail.returnedEurPalletsCount')}
                            fullWidth
                            disabled
                            type="numberLocale"
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <TextField
                            value={props.editData.DELIVERED_STANDARD_PALLETS_COUNT}
                            name="DELIVERED_STANDARD_PALLETS_COUNT"
                            label={translate('receivingDetail.deliveredStandardPalletsCount')}
                            fullWidth
                            disabled
                            type="numberLocale"
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <TextField
                            value={props.editData.RETURNED_STANDARD_PALLETS_COUNT}
                            name="RETURNED_STANDARD_PALLETS_COUNT"
                            label={translate('receivingDetail.returnedStandardPalletsCount')}
                            fullWidth
                            disabled
                            type="numberLocale"
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <TextField
                            value={props.editData.DELIVERED_OTHER_PALLETS_COUNT}
                            name="DELIVERED_OTHER_PALLETS_COUNT"
                            label={translate('receivingDetail.deliveredOtherPalletsCount')}
                            fullWidth
                            disabled
                            type="numberLocale"
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <TextField
                            value={props.editData.DELIVERED_BROKEN_PALLETS_COUNT}
                            name="DELIVERED_BROKEN_PALLETS_COUNT"
                            label={translate('receivingDetail.deliveredBrokenPalletsCount')}
                            fullWidth
                            disabled
                            type="numberLocale"
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <TextField
                            value={props.editData.SKLADON_NOTE}
                            name="SKLADON_NOTE"
                            label={translate('receivingDetail.skladonNote')}
                            fullWidth
                            disabled
                            multiline
                            inputProps={{ rows: 3 }}
                        />
                    </GridItem>
                </>
            )}
        </Grid>
    );
};

export default Detail;
